import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import {
  Activity,
  AlertCircle,
  Bot,
  Briefcase,
  ChevronRight,
  CircleDollarSign,
  Command,
  FileText,
  LayoutDashboard,
  LogOut,
  Mail,
  Megaphone,
  Moon,
  PlusCircle,
  Search,
  Settings,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Sun,
  User,
  Users,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "./theme-provider";
import { Button } from "./ui/button";
import { Dialog } from "./ui/dialog";
import { Dropdown } from "./ui/dropdown";
import { Input } from "./ui/input";
import { getCurrentUser } from "../utils/currentUser";
import { cacheClear } from "../utils/dataCache";
import { updateAdminProfile } from "../services/adminService";

const commandGroups = [
  {
    group: "Navigation",
    items: [
      { label: "Overview", href: "/", icon: LayoutDashboard },
      { label: "Scholars", href: "/scholars", icon: Sparkles, shortcut: "⌥1" },
      { label: "Users", href: "/users", icon: Users, shortcut: "⌥2" },
      { label: "Bookings", href: "/bookings", icon: Briefcase, shortcut: "⌥3" },
      { label: "Services", href: "/services", icon: ShieldCheck },
      { label: "Disputes", href: "/disputes", icon: AlertCircle, shortcut: "⌥4" },
      { label: "Finance", href: "/finance", icon: CircleDollarSign },
      { label: "Analytics", href: "/analytics", icon: Activity },
      { label: "Support", href: "/support", icon: User, shortcut: "⌥5" },
      { label: "Reports", href: "/reports", icon: FileText },
      { label: "Settings", href: "/settings", icon: Settings },
      { label: "Admins", href: "/admins", icon: ShieldAlert },
    ],
  },
  {
    group: "Quick Actions",
    items: [
      { label: "Create New Scholar", action: "create-scholar", icon: PlusCircle, color: "text-blue-500" },
      { label: "System Automation", href: "/automation", icon: Bot },
      { label: "Broadcast Message", href: "/messages", icon: Megaphone },
      { label: "Toggle Theme", action: "toggle-theme", icon: Moon },
      { label: "Sign Out", action: "logout", icon: LogOut },
    ],
  },
];

const ROLE_COLORS: Record<string, { bg: string; color: string }> = {
  SUPER_ADMIN: { bg: "hsl(45 100% 92%)",  color: "hsl(33 95% 40%)" },
  ADMIN:       { bg: "hsl(214 100% 94%)", color: "hsl(221 83% 50%)" },
  SUPPORT:     { bg: "hsl(142 72% 91%)",  color: "hsl(142 71% 35%)" },
  FINANCE:     { bg: "hsl(24 94% 93%)",   color: "hsl(24 95% 47%)" },
  AD_MANAGER:  { bg: "hsl(270 95% 94%)",  color: "hsl(270 91% 50%)" },
  OPERATIONS:  { bg: "hsl(199 89% 91%)",  color: "hsl(199 89% 38%)" },
};

const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: "Super Admin",
  ADMIN:       "Admin",
  SUPPORT:     "Support",
  FINANCE:     "Finance",
  AD_MANAGER:  "Ad Manager",
  OPERATIONS:  "Operations",
};

const Navbar = () => {
  const { theme, toggleTheme } = useTheme();
  const [openPalette, setOpenPalette] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);

  const currentUser = getCurrentUser();

  const [availabilityStatus, setAvailabilityStatus] = useState(
    localStorage.getItem("availabilityStatus") || currentUser.raw?.availabilityStatus || "ONLINE"
  );
  const [supportAvatarUrl, setSupportAvatarUrl] = useState(
    localStorage.getItem("supportAvatarUrl") || currentUser.raw?.supportAvatarUrl || ""
  );

  const handleUpdateStatus = async (status: string) => {
    setAvailabilityStatus(status);
    localStorage.setItem("availabilityStatus", status);
    await updateAdminProfile({ availabilityStatus: status });
  };

  const handleSaveAvatar = async () => {
    localStorage.setItem("supportAvatarUrl", supportAvatarUrl);
    await updateAdminProfile({ supportAvatarUrl });
    alert("Support avatar updated successfully!");
  };
  // Build avatar initials from name
  const initials = (currentUser.name ?? "A")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase() || "AD";

  // Today formatted nicely
  const today = new Date();
  const todayStr = today.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const roleColor = ROLE_COLORS[currentUser.role ?? ""] ?? { bg: "hsl(var(--muted))", color: "hsl(var(--foreground))" };
  const roleLabel = ROLE_LABELS[currentUser.role ?? ""] ?? currentUser.role ?? "Admin";

  // Close profile popover on outside click
  useEffect(() => {
    const onOutsideClick = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setShowProfile(false);
      }
    };
    if (showProfile) document.addEventListener("mousedown", onOutsideClick);
    return () => document.removeEventListener("mousedown", onOutsideClick);
  }, [showProfile]);

  const flatItems = useMemo(() => {
    const items: any[] = [];
    commandGroups.forEach((group) => {
      group.items.forEach((item) => {
        if (item.label.toLowerCase().includes(query.toLowerCase())) {
          items.push({ ...item, group: group.group });
        }
      });
    });
    return items;
  }, [query]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const handleAction = useCallback((item: any) => {
    if (item.href) {
      navigate(item.href);
    } else if (item.action === "toggle-theme") {
      toggleTheme();
    } else if (item.action === "logout") {
      localStorage.removeItem("token");
      cacheClear();
      navigate("/login");
    } else if (item.action === "create-scholar") {
      navigate("/scholars?action=create");
    }
    setOpenPalette(false);
    setQuery("");
  }, [navigate, toggleTheme]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!event.key) return;
      const isMod = event.metaKey || event.ctrlKey;
      const isAlt = event.altKey;
      const key = event.key.toLowerCase();

      // Toggle Palette (Ctrl+K)
      if (isMod && key === "k") {
        event.preventDefault();
        setOpenPalette((prev) => !prev);
        return;
      }

      // Quick Navigation Shortcuts (Alt + 1-5)
      if (isAlt && !isMod) {
        const shortcuts: Record<string, string> = {
          "1": "/scholars",
          "2": "/users",
          "3": "/bookings",
          "4": "/disputes",
          "5": "/support",
        };

        if (shortcuts[key]) {
          event.preventDefault();
          navigate(shortcuts[key]);
          setOpenPalette(false);
          return;
        }
      }

      if (openPalette) {
        if (event.key === "ArrowDown") {
          event.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % flatItems.length);
        } else if (event.key === "ArrowUp") {
          event.preventDefault();
          setSelectedIndex((prev) => (prev - 1 + flatItems.length) % flatItems.length);
        } else if (event.key === "Enter") {
          event.preventDefault();
          if (flatItems[selectedIndex]) {
            handleAction(flatItems[selectedIndex]);
          }
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [openPalette, flatItems, selectedIndex, navigate, handleAction]);

  useEffect(() => {
    if (openPalette) {
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [openPalette]);

  return (
    <>
      <nav
        style={{
          minHeight: 68,
          borderBottom: "1px solid hsl(var(--border))",
          background: "hsl(var(--background))",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          padding: "12px 20px",
          position: "sticky",
          top: 0,
          zIndex: 30,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, width: "min(560px, 100%)" }}>
          <div style={{ position: "relative", flex: 1 }}>
            <Search
              size={15}
              style={{ position: "absolute", left: 12, top: 12, color: "hsl(var(--muted-foreground))" }}
            />
            <Input
              placeholder="Search scholars (e.g. SID1785430890), reports, bookings"
              style={{ width: "100%", paddingLeft: 34, paddingRight: 72 }}
              onClick={() => setOpenPalette(true)}
              readOnly
            />
            <div
              style={{
                position: "absolute",
                right: 8,
                top: 8,
                display: "flex",
                gap: 4,
                alignItems: "center",
                padding: "4px 8px",
                background: "hsl(var(--muted))",
                borderRadius: 6,
                fontSize: 10,
                color: "hsl(var(--muted-foreground))",
                pointerEvents: "none",
                fontWeight: 600,
                border: "1px solid hsl(var(--border))",
              }}
            >
              <Command size={10} />K
            </div>
          </div>
          <Dropdown
            aria-label="Date range"
            defaultValue="30d"
            options={[
              { value: "7d", label: "7 days" },
              { value: "30d", label: "30 days" },
              { value: "90d", label: "90 days" },
            ]}
          />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Button variant="secondary" onClick={toggleTheme} style={{ borderRadius: 12, width: 40, padding: 0 }}>
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </Button>

          {/* User profile avatar + popover */}
          <div ref={profileRef} style={{ position: "relative" }}>
            <button
              onClick={() => setShowProfile((v) => !v)}
              title="View profile"
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                background: "hsl(var(--primary))",
                border: showProfile ? "2px solid hsl(var(--primary) / 0.6)" : "2px solid transparent",
                display: "grid",
                placeItems: "center",
                color: "white",
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
                transition: "all 160ms ease",
                outline: "none",
                boxShadow: showProfile ? "0 0 0 3px hsl(var(--primary) / 0.18)" : "none",
              }}
            >
              {initials}
            </button>

            {showProfile && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 10px)",
                  right: 0,
                  width: 280,
                  borderRadius: 16,
                  border: "1px solid hsl(var(--border))",
                  background: "hsl(var(--card))",
                  boxShadow: "0 16px 40px hsl(0 0% 0% / 0.14), 0 4px 12px hsl(0 0% 0% / 0.08)",
                  padding: 0,
                  overflow: "hidden",
                  zIndex: 50,
                  animation: "fadeInDown 160ms ease",
                }}
              >
                {/* Header strip */}
                <div
                  style={{
                    background: "hsl(var(--primary))",
                    padding: "20px 18px 28px",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      width: 52,
                      height: 52,
                      borderRadius: 14,
                      background: "white",
                      color: "hsl(var(--primary))",
                      display: "grid",
                      placeItems: "center",
                      fontWeight: 800,
                      fontSize: 18,
                      marginBottom: 10,
                      boxShadow: "0 4px 12px hsl(0 0% 0% / 0.15)",
                    }}
                  >
                    {initials}
                  </div>
                  <div style={{ color: "white", fontWeight: 700, fontSize: 16, lineHeight: 1.3 }}>
                    {currentUser.name ?? "Admin User"}
                  </div>
                  <div style={{ color: "hsl(var(--primary-foreground) / 0.75)", fontSize: 12, marginTop: 2 }}>
                    {currentUser.email ?? "—"}
                  </div>
                </div>

                {/* Body */}
                <div style={{ padding: "14px 18px 18px" }}>
                  {/* Role badge */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        padding: "3px 10px",
                        borderRadius: 999,
                        background: roleColor.bg,
                        color: roleColor.color,
                        letterSpacing: "0.02em",
                        textTransform: "uppercase",
                      }}
                    >
                      {roleLabel}
                    </span>
                  </div>

                  {/* Date row */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "10px 12px",
                      borderRadius: 10,
                      background: "hsl(var(--secondary))",
                      fontSize: 12,
                      color: "hsl(var(--muted-foreground))",
                      marginBottom: 14,
                    }}
                  >
                    <span style={{ fontSize: 15 }}>📅</span>
                    <span>{todayStr}</span>
                  </div>

                  {/* Email row */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      fontSize: 12,
                      color: "hsl(var(--muted-foreground))",
                      marginBottom: 16,
                      padding: "0 2px",
                    }}
                  >
                    <Mail size={13} />
                    <span style={{ wordBreak: "break-all" }}>{currentUser.email ?? "Not available"}</span>
                  </div>

                  {(currentUser.role === 'SUPPORT' || currentUser.department === 'SUPPORT' || currentUser.role === 'ADMIN') && (
                    <>
                      {/* Separator */}
                      <div style={{ height: 1, background: "hsl(var(--border))", margin: "12px 0" }} />

                      {/* Availability Status Select */}
                      <div style={{ marginBottom: 14 }}>
                        <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: "hsl(var(--muted-foreground))", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          Availability Status
                        </label>
                        <select
                          value={availabilityStatus}
                          onChange={(e) => handleUpdateStatus(e.target.value)}
                          style={{
                            width: "100%",
                            padding: "8px 12px",
                            borderRadius: 10,
                            border: "1px solid hsl(var(--border))",
                            background: "hsl(var(--background))",
                            color: "hsl(var(--foreground))",
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: "pointer",
                            outline: "none",
                          }}
                        >
                          <option value="ONLINE">🟢 Online</option>
                          <option value="BREAK">🟠 Break</option>
                          <option value="OFFLINE">🔴 Offline</option>
                        </select>
                      </div>

                      {/* Support Avatar Input */}
                      <div style={{ marginBottom: 16 }}>
                        <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: "hsl(var(--muted-foreground))", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          Support Avatar URL
                        </label>
                        <div style={{ display: "flex", gap: 6 }}>
                          <input
                            type="text"
                            value={supportAvatarUrl}
                            onChange={(e) => setSupportAvatarUrl(e.target.value)}
                            placeholder="https://example.com/avatar.jpg"
                            style={{
                              flex: 1,
                              padding: "8px 12px",
                              borderRadius: 10,
                              border: "1px solid hsl(var(--border))",
                              background: "hsl(var(--background))",
                              color: "hsl(var(--foreground))",
                              fontSize: 12,
                              outline: "none",
                            }}
                          />
                          <button
                            onClick={handleSaveAvatar}
                            style={{
                              padding: "8px 12px",
                              borderRadius: 10,
                              background: "hsl(var(--primary))",
                              color: "white",
                              border: "none",
                              fontSize: 12,
                              fontWeight: 700,
                              cursor: "pointer",
                            }}
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Sign out */}
                  <button
                    onClick={() => {
                      localStorage.removeItem("token");
                      cacheClear();
                      navigate("/login");
                    }}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      padding: "9px 14px",
                      borderRadius: 10,
                      border: "1px solid hsl(var(--destructive) / 0.35)",
                      background: "hsl(var(--destructive) / 0.07)",
                      color: "hsl(var(--destructive))",
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                      transition: "all 150ms ease",
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.background = "hsl(var(--destructive) / 0.15)";
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.background = "hsl(var(--destructive) / 0.07)";
                    }}
                  >
                    <LogOut size={14} /> Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>

      <Dialog open={openPalette} onOpenChange={setOpenPalette} title="Command Palette">
        <div style={{ position: "relative" }}>
          <Search
            size={18}
            style={{ position: "absolute", left: 14, top: 14, color: "hsl(var(--muted-foreground))" }}
          />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search commands or pages..."
            style={{
              width: "100%",
              padding: "14px 14px 14px 44px",
              background: "hsl(var(--secondary) / 0.5)",
              border: "1px solid hsl(var(--border))",
              borderRadius: 14,
              fontSize: 15,
              outline: "none",
              color: "hsl(var(--foreground))",
            }}
          />
        </div>

        <div
          className="soft-scroll"
          style={{
            marginTop: 16,
            maxHeight: "60vh",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 4,
            padding: "4px 2px",
          }}
        >
          {flatItems.length > 0 ? (
            (() => {
              let currentGroup = "";
              return flatItems.map((item, index) => {
                const showGroup = item.group !== currentGroup;
                currentGroup = item.group;
                const Icon = item.icon;
                const selected = index === selectedIndex;

                return (
                  <div key={item.label + item.href + item.action}>
                    {showGroup && (
                      <div
                        style={{
                          padding: "12px 12px 6px",
                          fontSize: 11,
                          fontWeight: 600,
                          color: "hsl(var(--muted-foreground))",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                        }}
                      >
                        {item.group}
                      </div>
                    )}
                    <button
                      onClick={() => handleAction(item)}
                      onMouseEnter={() => setSelectedIndex(index)}
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "10px 14px",
                        borderRadius: 12,
                        background: selected ? "hsl(var(--primary) / 0.1)" : "transparent",
                        border: `1px solid ${selected ? "hsl(var(--primary) / 0.2)" : "transparent"}`,
                        cursor: "pointer",
                        transition: "all 150ms ease",
                        textAlign: "left",
                        color: selected ? "hsl(var(--foreground))" : "hsl(var(--muted-foreground))",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 8,
                            background: selected ? "hsl(var(--primary))" : "hsl(var(--secondary))",
                            color: selected ? "white" : "hsl(var(--foreground))",
                            display: "grid",
                            placeItems: "center",
                            transition: "all 150ms ease",
                          }}
                        >
                          <Icon size={16} />
                        </div>
                        <span
                          style={{
                            fontSize: 14,
                            fontWeight: selected ? 600 : 500,
                            color: selected ? "hsl(var(--foreground))" : "hsl(var(--muted-foreground))",
                          }}
                        >
                          {item.label}
                        </span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {item.shortcut && (
                          <kbd
                            style={{
                              fontSize: 10,
                              background: selected ? "hsl(var(--primary) / 0.2)" : "hsl(var(--muted))",
                              padding: "2px 6px",
                              borderRadius: 4,
                              color: selected ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))",
                              fontWeight: 600,
                              border: `1px solid ${selected ? "hsl(var(--primary) / 0.3)" : "hsl(var(--border))"}`,
                            }}
                          >
                            {item.shortcut}
                          </kbd>
                        )}
                        {selected && <ChevronRight size={14} style={{ color: "hsl(var(--primary))" }} />}
                      </div>
                    </button>
                  </div>
                );
              });
            })()
          ) : (
            <div style={{ padding: "32px 16px", textAlign: "center" }}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 16,
                  background: "hsl(var(--muted))",
                  display: "grid",
                  placeItems: "center",
                  margin: "0 auto 12px",
                  color: "hsl(var(--muted-foreground))",
                }}
              >
                <Search size={20} />
              </div>
              <p style={{ margin: 0, color: "hsl(var(--muted-foreground))", fontSize: 14 }}>
                No results found for "{query}"
              </p>
            </div>
          )}
        </div>

        <div
          style={{
            marginTop: 16,
            paddingTop: 12,
            borderTop: "1px solid hsl(var(--border))",
            display: "flex",
            gap: 12,
            fontSize: 11,
            color: "hsl(var(--muted-foreground))",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <kbd
              style={{
                background: "hsl(var(--muted))",
                padding: "2px 4px",
                borderRadius: 4,
                border: "1px solid hsl(var(--border))",
              }}
            >
              ↑↓
            </kbd>{" "}
            to navigate
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <kbd
              style={{
                background: "hsl(var(--muted))",
                padding: "2px 4px",
                borderRadius: 4,
                border: "1px solid hsl(var(--border))",
              }}
            >
              Enter
            </kbd>{" "}
            to select
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <kbd
              style={{
                background: "hsl(var(--muted))",
                padding: "2px 4px",
                borderRadius: 4,
                border: "1px solid hsl(var(--border))",
              }}
            >
              Esc
            </kbd>{" "}
            to close
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default Navbar;
